/**
 * Trip Detail Page
 * Shows trip details and allows pilots to complete flight legs
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTripById, Trip, adminCompleteTrip } from '../services/tripService';
import { authFetch } from '../services/authService';
import { API_BASE_URL, SERVER_BASE_URL } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

export const TripDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<number | null>(null);
  const [confirmingSubmit, setConfirmingSubmit] = useState<number | null>(null);
  const [completingTrip, setCompletingTrip] = useState(false);

  // Form state for each flight
  const [flightForms, setFlightForms] = useState<Record<number, {
    actualAirTime: string;
    actualFlightTime: string;
    pilotNotes: string;
    reason: string; // Required for approved flights
  }>>({});

  useEffect(() => {
    loadTrip();
  }, [id]);

  const loadTrip = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getTripById(parseInt(id));
      setTrip(data);

      // Initialize form state
      const forms: Record<number, any> = {};
      data.flights.forEach(flight => {
        forms[flight.id] = {
          actualAirTime: flight.actualAirTime?.toString() || '',
          actualFlightTime: flight.actualFlightTime?.toString() || '',
          pilotNotes: flight.pilotNotes || '',
          reason: '' // Required for approved flights
        };
      });
      setFlightForms(forms);
    } catch (err: any) {
      setError(err.message || 'Failed to load trip');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (flightId: number, field: string, value: string) => {
    setFlightForms(prev => ({
      ...prev,
      [flightId]: {
        ...prev[flightId],
        [field]: value
      }
    }));
  };

  const handleUpdateFlight = async (flightId: number) => {
    const form = flightForms[flightId];
    const flight = trip?.flights.find(f => f.id === flightId);

    if (!form.actualAirTime || !form.actualFlightTime) {
      showError('Missing data', 'Please enter both air time and flight time');
      return;
    }

    // Check if reason is required (approved or paid flights)
    if (flight && (flight.status === 'APPROVED' || flight.status === 'PAID')) {
      if (!form.reason || form.reason.trim() === '') {
        showError('Reason required', 'A reason is required when editing approved flights');
        return;
      }
    }

    try {
      setSubmitting(flightId);

      const requestBody: any = {
        actualAirTime: parseFloat(form.actualAirTime),
        actualFlightTime: parseFloat(form.actualFlightTime),
        pilotNotes: form.pilotNotes,
        status: 'IN_PROGRESS'
      };

      // Include reason if provided (required for approved flights)
      if (form.reason && form.reason.trim() !== '') {
        requestBody.reason = form.reason;
      }

      const response = await authFetch(`${API_BASE_URL}/flights/trip/${flightId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update flight');
      }

      // Clear reason field after successful update
      setFlightForms(prev => ({
        ...prev,
        [flightId]: {
          ...prev[flightId],
          reason: ''
        }
      }));

      // Reload trip to see updated data
      await loadTrip();
      showSuccess('Flight updated');
    } catch (err: any) {
      showError('Update failed', err.message || 'Failed to update flight');
    } finally {
      setSubmitting(null);
    }
  };

  const handleSubmitFlight = async (flightId: number) => {
    const form = flightForms[flightId];

    if (!form.actualAirTime || !form.actualFlightTime) {
      showError('Missing data', 'Please enter both air time and flight time before submitting');
      return;
    }

    if (confirmingSubmit !== flightId) {
      setConfirmingSubmit(flightId);
      return;
    }
    setConfirmingSubmit(null);

    try {
      setSubmitting(flightId);

      const response = await authFetch(`${API_BASE_URL}/flights/trip/${flightId}/submit`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to submit flight');
      }

      await loadTrip();
      showSuccess('Flight submitted for approval');
    } catch (err: any) {
      showError('Submit failed', err.message || 'Failed to submit flight');
    } finally {
      setSubmitting(null);
    }
  };

  const handlePhotoUpload = async (flightId: number, file: File | undefined) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await authFetch(`${API_BASE_URL}/flights/trip/${flightId}/photo`, {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set Content-Type with boundary
      });

      if (!response.ok) {
        throw new Error('Failed to upload photo');
      }

      await loadTrip();
      showSuccess('Photo uploaded');
    } catch (err: any) {
      showError('Upload failed', err.message || 'Failed to upload photo');
    }
  };

  const handleApproveFlight = async (flightId: number) => {
    try {
      setSubmitting(flightId);
      const response = await authFetch(`${API_BASE_URL}/flights/trip/${flightId}/approve`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to approve flight');
      }

      await loadTrip();
      showSuccess('Flight approved');
    } catch (err: any) {
      showError('Approval failed', err.message || 'Failed to approve flight');
    } finally {
      setSubmitting(null);
    }
  };

  const handleCompleteTrip = async () => {
    if (!trip) return;
    setCompletingTrip(true);
    try {
      const result = await adminCompleteTrip(trip.id);
      showSuccess(
        'Trip completed',
        `${result.totalFlights} flights, ${result.totalFlightTime.toFixed(1)}h total`
      );
      await loadTrip();
    } catch (err: any) {
      showError('Completion failed', err.message || 'Failed to complete trip');
    } finally {
      setCompletingTrip(false);
    }
  };

  /**
   * Extract operational notes from the email body
   * Looks for catering, client info, special instructions, customs, departure slots
   */
  const extractOpsNotes = (emailBody: string | null | undefined): Array<{ label: string; text: string }> => {
    if (!emailBody) return [];
    const notes: Array<{ label: string; text: string }> = [];

    // Client info
    const clientMatch = emailBody.match(/Client:\s*\n\s*(.+?)(?:\n|$)/i);
    if (clientMatch) {
      notes.push({ label: 'Client', text: clientMatch[1]!.trim() });
    }

    // Passenger contact / name after client
    const paxNameMatch = emailBody.match(/Client:\s*\n\s*.+?\n\s*(.+?)(?:\n|$)/i);
    if (paxNameMatch && paxNameMatch[1]!.trim().length > 2 && !paxNameMatch[1]!.includes('Aircraft')) {
      notes.push({ label: 'Contact', text: paxNameMatch[1]!.trim() });
    }

    // Catering requests
    const cateringLines: string[] = [];
    const lines = emailBody.split('\n');
    let inCatering = false;
    for (const line of lines) {
      if (/catering|muffin|bagel|sandwich|food|lunch|snack/i.test(line)) {
        inCatering = true;
      }
      if (inCatering && line.trim().match(/^\d+\.\s+/)) {
        cateringLines.push(line.trim());
      }
      if (inCatering && line.trim() === '' && cateringLines.length > 0) {
        inCatering = false;
      }
    }
    if (cateringLines.length > 0) {
      notes.push({ label: 'Catering', text: cateringLines.join('\n') });
    }

    // Departure slot / special departure instructions
    const slotMatch = emailBody.match(/((?:depart|dep).*?slot.*?(?:\.|$))/i);
    if (slotMatch) {
      notes.push({ label: 'Departure Note', text: slotMatch[1]!.trim() });
    } else {
      const deptMatch = emailBody.match(/(Please depart.*?(?:\.|$))/i);
      if (deptMatch) {
        notes.push({ label: 'Departure Note', text: deptMatch[1]!.trim() });
      }
    }

    // Customs / Canpass
    const customsMatch = emailBody.match(/((?:canpass|customs|eapis|cbsa).*?(?:\.|$))/i);
    if (customsMatch) {
      notes.push({ label: 'Customs', text: customsMatch[1]!.trim() });
    }

    // Any other special notes (fax issues, warnings, etc.)
    const faxMatch = emailBody.match(/(fax.*?(?:\.|$))/i);
    if (faxMatch) {
      notes.push({ label: 'Note', text: faxMatch[1]!.trim() });
    }

    return notes;
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700';
      case 'SUBMITTED':
        return 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700';
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700';
      case 'PAID':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Trip not found'}</p>
          <button
            onClick={() => navigate('/trips')}
            className="text-primary hover:text-primary/80 underline"
          >
            Back to Trips
          </button>
        </div>
      </div>
    );
  }

  // Parse AI-extracted data once for use across the page
  let aiData: { specialNotes?: string; flightLegs?: Array<{ from: string; to: string; date: string; departureTime: string; arrivalTime: string; flightTime: number }> } | null = null;
  try {
    if (trip.aiExtractedData) {
      aiData = JSON.parse(trip.aiExtractedData);
    }
  } catch { /* ignore parse errors */ }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/trips')}
            className="text-primary hover:text-primary/80 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Trips
          </button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Trip {trip.qNumber}</h1>
              <p className="mt-2 text-muted-foreground">
                {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(trip.status)}`}>
              {formatStatus(trip.status)}
            </span>
          </div>
        </div>

        {/* Trip Info Card */}
        <div className="bg-card rounded-lg shadow border border-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Trip Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Aircraft</p>
              <p className="font-medium text-foreground">{trip.tailNumber || 'N/A'}</p>
              <p className="text-sm text-muted-foreground">{trip.aircraftType || ''}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Crew</p>
              {trip.crewMembers.map(cm => (
                <p key={cm.id} className="font-medium text-foreground">
                  {cm.user.firstName} {cm.user.lastName}
                  <span className="text-sm text-muted-foreground ml-2">({cm.role})</span>
                </p>
              ))}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Flight Legs</p>
              <p className="font-medium text-foreground">{trip.flights.length} legs</p>
            </div>
          </div>
        </div>

        {/* Admin: Complete Trip banner */}
        {user?.role === 'admin' &&
          trip.status !== 'COMPLETED' &&
          trip.flights.length > 0 &&
          trip.flights.every(f => f.status === 'APPROVED' || f.status === 'PAID' || f.status === 'COMPLETED') && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-5 mb-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-900 dark:text-green-300">All flights approved</h3>
                <p className="text-sm text-green-700 dark:text-green-400 mt-0.5">
                  Ready to finalize — this will calculate pay, create logbook entries, and send the ops report.
                </p>
              </div>
              <button
                onClick={handleCompleteTrip}
                disabled={completingTrip}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {completingTrip ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Completing...
                  </>
                ) : (
                  'Complete Trip'
                )}
              </button>
            </div>
          )}

        {/* Operations Briefing */}
        {(() => {
          const opsNotes = extractOpsNotes(trip.emailBody);
          const hasContent = opsNotes.length > 0 || aiData?.specialNotes || aiData?.flightLegs;

          return hasContent ? (
            <div className="bg-card rounded-lg shadow border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Operations Briefing</h2>
              <div className="space-y-3">
                {opsNotes.map((note, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-md whitespace-nowrap h-fit">
                      {note.label}
                    </span>
                    <p className="text-sm text-foreground whitespace-pre-line">{note.text}</p>
                  </div>
                ))}

                {/* AI-extracted special notes */}
                {aiData?.specialNotes && (
                  <div className="flex gap-3">
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30 px-2 py-1 rounded-md whitespace-nowrap h-fit">
                      Ops Notes
                    </span>
                    <p className="text-sm text-foreground whitespace-pre-line">{aiData.specialNotes}</p>
                  </div>
                )}

                {/* AI-extracted flight times per leg */}
                {aiData?.flightLegs && aiData.flightLegs.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Scheduled Times (from briefing)</p>
                    <div className="grid gap-2">
                      {aiData.flightLegs.map((leg, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="font-medium text-foreground">{leg.from} → {leg.to}</span>
                          <span className="text-muted-foreground">ETD {leg.departureTime}L</span>
                          <span className="text-muted-foreground">ETA {leg.arrivalTime}L</span>
                          {leg.flightTime && (
                            <span className="text-muted-foreground">({leg.flightTime}h)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null;
        })()}

        {/* Trip Documents */}
        {(trip.attachments && trip.attachments.length > 0) || trip.email ? (
          <div className="bg-card rounded-lg shadow border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Documents</h2>

            {/* Email Source Info */}
            {trip.email && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <span className="font-medium">Created from email:</span> {trip.email.subject}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  From: {trip.email.from} | Received: {new Date(trip.email.receivedAt).toLocaleString()}
                </p>
              </div>
            )}

            {/* Attachments grouped by type */}
            {trip.attachments && trip.attachments.length > 0 && (() => {
              const groups: Record<string, typeof trip.attachments> = {};
              for (const att of trip.attachments!) {
                const key = att.description || 'Document';
                if (!groups[key]) groups[key] = [];
                groups[key]!.push(att);
              }

              const groupOrder = ['Crew Briefing', 'Airport Factsheet', 'Customs Document', 'Canpass Report', 'Hotel Confirmation', 'Document'];

              return (
                <div className="space-y-4">
                  {groupOrder.filter(g => groups[g]).map(groupName => (
                    <div key={groupName}>
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">{groupName}s</h3>
                      <div className="space-y-2">
                        {groups[groupName]!.map((attachment) => (
                          <a
                            key={attachment.id}
                            href={`${SERVER_BASE_URL}${attachment.storagePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-foreground">{attachment.filename}</p>
                                <p className="text-xs text-muted-foreground">{(attachment.size / 1024).toFixed(1)} KB</p>
                              </div>
                            </div>
                            <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ) : null}

        {/* Flight Legs */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-foreground">Flight Legs</h2>

          {trip.flights.length === 0 ? (
            <div className="bg-card rounded-lg shadow border border-border p-8 text-center">
              <p className="text-muted-foreground">No flight legs in this trip yet</p>
            </div>
          ) : trip.flights.map((flight) => (
            <div key={flight.id} className="bg-card rounded-lg shadow border border-border">
              {/* Flight Header */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Leg {flight.legNumber}: {flight.fromAirport} → {flight.toAirport}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatDate(flight.date)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(flight.status)}`}>
                    {formatStatus(flight.status)}
                  </span>
                </div>

                {/* Schedule — use AI-extracted times as fallback */}
                {(() => {
                  const aiLeg = aiData?.flightLegs?.find(
                    (l: any) => l.from === flight.fromAirport && l.to === flight.toAirport
                  );
                  const etd = flight.estimatedDeparture || (aiLeg?.departureTime ? `${aiLeg.departureTime}L` : null);
                  const eta = flight.estimatedArrival || (aiLeg?.arrivalTime ? `${aiLeg.arrivalTime}L` : null);
                  const ete = aiLeg?.flightTime;

                  return (
                    <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border">
                      <div>
                        <p className="text-xs text-muted-foreground">ETD</p>
                        <p className="font-semibold text-foreground text-lg">{etd || 'TBD'}</p>
                      </div>
                      <div className="text-muted-foreground">→</div>
                      <div>
                        <p className="text-xs text-muted-foreground">ETA</p>
                        <p className="font-semibold text-foreground text-lg">{eta || 'TBD'}</p>
                      </div>
                      {ete && (
                        <div className="ml-auto">
                          <p className="text-xs text-muted-foreground">ETE</p>
                          <p className="font-medium text-foreground">{ete}h</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Flight Form */}
              {flight.status !== 'SUBMITTED' && flight.status !== 'APPROVED' && (
                <div className="p-6 bg-muted/50">
                  <h4 className="font-medium text-foreground mb-4">Complete Flight Data</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Actual Air Time (hours) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="24"
                        value={flightForms[flight.id]?.actualAirTime || ''}
                        onChange={(e) => handleFormChange(flight.id, 'actualAirTime', e.target.value)}
                        className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="e.g., 1.2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Actual Flight Time (hours) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="24"
                        value={flightForms[flight.id]?.actualFlightTime || ''}
                        onChange={(e) => handleFormChange(flight.id, 'actualFlightTime', e.target.value)}
                        className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="e.g., 1.3"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Pilot Notes
                    </label>
                    <textarea
                      rows={3}
                      value={flightForms[flight.id]?.pilotNotes || ''}
                      onChange={(e) => handleFormChange(flight.id, 'pilotNotes', e.target.value)}
                      className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Any additional notes about this flight..."
                    />
                  </div>

                  {/* Reason field - Required for paid flights */}
                  {flight.status === 'PAID' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Reason for Edit *
                        <span className="text-xs text-muted-foreground ml-2">(Required for paid flights)</span>
                      </label>
                      <textarea
                        rows={2}
                        value={flightForms[flight.id]?.reason || ''}
                        onChange={(e) => handleFormChange(flight.id, 'reason', e.target.value)}
                        className="w-full px-3 py-2 border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Why are you editing this paid flight? (e.g., 'Logbook correction', 'Incorrect time entry')"
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Logbook Photo
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(flight.id, e.target.files?.[0])}
                      className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {flight.logbookPhotoUrl && (
                      <div className="mt-2">
                        <img
                          src={`${SERVER_BASE_URL}${flight.logbookPhotoUrl}`}
                          alt="Logbook entry"
                          className="max-w-xs rounded border border-border"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleUpdateFlight(flight.id)}
                      disabled={submitting === flight.id}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting === flight.id ? 'Saving...' : 'Save Progress'}
                    </button>
                    <button
                      onClick={() => handleSubmitFlight(flight.id)}
                      disabled={submitting === flight.id}
                      className={`px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                        confirmingSubmit === flight.id
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-green-600 dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600'
                      }`}
                    >
                      {submitting === flight.id ? 'Submitting...' : confirmingSubmit === flight.id ? 'Confirm Submit?' : 'Submit for Approval'}
                    </button>
                    {confirmingSubmit === flight.id && (
                      <button
                        onClick={() => setConfirmingSubmit(null)}
                        className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Completed Flight Display */}
              {(flight.status === 'SUBMITTED' || flight.status === 'APPROVED') && flight.actualAirTime && (
                <div className="p-6 bg-green-50 dark:bg-green-900/20">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-green-700 dark:text-green-400">Actual Air Time</p>
                      <p className="font-semibold text-green-900 dark:text-green-300">{flight.actualAirTime} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700 dark:text-green-400">Actual Flight Time</p>
                      <p className="font-semibold text-green-900 dark:text-green-300">{flight.actualFlightTime} hours</p>
                    </div>
                    {flight.pilotNotes && (
                      <div className="md:col-span-3">
                        <p className="text-sm text-green-700 dark:text-green-400">Notes</p>
                        <p className="text-green-900 dark:text-green-300">{flight.pilotNotes}</p>
                      </div>
                    )}
                  </div>

                  {/* Admin approve button */}
                  {(user?.role === 'admin' || user?.role === 'scheduler') && flight.status === 'SUBMITTED' && (
                    <div className="mt-4">
                      <button
                        onClick={() => handleApproveFlight(flight.id)}
                        disabled={submitting === flight.id}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
                      >
                        {submitting === flight.id ? 'Approving...' : 'Approve Flight'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
